/**
 * The shape catalogue.
 *
 * Every path is drawn in the same 1200x120 box and closes along the BOTTOM
 * edge, so the filled area is the lower half of the box. That single
 * convention is what lets one CSS flip turn any shape into its top-edge
 * counterpart — without it each shape would need a second, hand-drawn path
 * and the two would drift apart the first time one was adjusted.
 *
 * `preserveAspectRatio="none"` on the SVG lets the height attribute squash or
 * stretch the box freely, which is what makes a 20px sliver and a 200px sweep
 * the same path.
 *
 * A shape may carry a `layers` array instead of a single `path`: each layer is
 * drawn in order at its own opacity, which is what gives the stacked waves
 * their depth. One path is just the one-layer case.
 */

import { __ } from '@wordpress/i18n';

export const SHAPES = {
  waves: {
    label: __('Waves', 'brandy-blocks'),
    top: 62.6,
    layers: [
      {
        d: 'M0,80L48,74.7C96,69,192,59,288,64C384,69,480,91,576,96C672,101,768,91,864,80C960,69,1056,59,1152,64L1200,69L1200,120L0,120Z',
        opacity: 0.35,
      },
      {
        d: 'M0,96L48,90.7C96,85,192,75,288,77.3C384,80,480,96,576,101.3C672,107,768,101,864,93.3C960,85,1056,75,1152,80L1200,85L1200,120L0,120Z',
        opacity: 0.6,
      },
      {
        d: 'M0,107L48,104C96,101,192,96,288,98.7C384,101,480,112,576,114.7C672,117,768,112,864,106.7C960,101,1056,96,1152,98.7L1200,101L1200,120L0,120Z',
        opacity: 1,
      },
    ],
  },
  wave: {
    label: __('Wave', 'brandy-blocks'),
    top: 48.8,
    path: 'M0,64L60,58.7C120,53,240,43,360,53.3C480,64,600,96,720,101.3C840,107,960,85,1080,74.7L1200,64L1200,120L0,120Z',
  },
  curve: {
    label: __('Curve', 'brandy-blocks'),
    top: 15,
    path: 'M0,120V60C300,0,900,0,1200,60V120Z',
  },
  'curve-asymmetric': {
    label: __('Asymmetric curve', 'brandy-blocks'),
    top: 40,
    path: 'M0,120V40C400,120,800,0,1200,72V120Z',
  },
  tilt: {
    label: __('Tilt', 'brandy-blocks'),
    top: 0,
    path: 'M0,120L1200,0V120Z',
  },
  triangle: {
    label: __('Triangle', 'brandy-blocks'),
    top: 0,
    path: 'M0,120L600,0L1200,120Z',
  },
  arrow: {
    label: __('Arrow', 'brandy-blocks'),
    top: 0,
    path: 'M0,0L520,0L600,64L680,0L1200,0V120H0Z',
  },
  split: {
    label: __('Split', 'brandy-blocks'),
    top: 0,
    path: 'M0,0H540L600,84L660,0H1200V120H0Z',
  },
  mountains: {
    label: __('Mountains', 'brandy-blocks'),
    top: 28,
    layers: [
      { d: 'M0,120L260,36L470,120L760,28L1000,120L1200,52V120Z', opacity: 0.4 },
      { d: 'M0,120L200,64L420,120L700,58L980,120L1200,78V120Z', opacity: 1 },
    ],
  },
  zigzag: {
    label: __('Zigzag', 'brandy-blocks'),
    top: 40,
    path: 'M0,120L200,40L400,120L600,40L800,120L1000,40L1200,120Z',
  },
  book: {
    label: __('Book', 'brandy-blocks'),
    top: 16,
    path: 'M0,120V16C200,96,400,96,600,56C800,16,1000,16,1200,64V120Z',
  },
};

export const DEFAULT_SHAPE = 'waves';

/**
 * Where a shape's ink starts inside the 1200x120 box.
 *
 * Most of these paths draw only in the lower part of the box — the default
 * waves use barely half of it. Stretching the whole box to the requested
 * height therefore grew the empty band as fast as the shape, so a taller
 * divider was mostly blank with a stretched sliver along the bottom, which
 * reads as a shape that has been cut off. Cropping the viewBox to the ink
 * means height scales what you can actually see.
 *
 * Recompute after editing a path: render it and read `path.getBBox().y`.
 *
 * @param {string} name Shape key.
 * @return {number} Top of the ink in viewBox units.
 */
export function getShapeTop(name) {
  return (SHAPES[name] || SHAPES[DEFAULT_SHAPE]).top || 0;
}

/**
 * A shape's layers, with a single-path shape normalised to one opaque layer so
 * callers never branch on which kind they were given.
 *
 * @param {string} name Shape key.
 * @return {Array} List of { d, opacity }.
 */
export function getShapeLayers(name) {
  const shape = SHAPES[name] || SHAPES[DEFAULT_SHAPE];

  if (shape.layers) {
    return shape.layers;
  }

  return [{ d: shape.path, opacity: 1 }];
}
